import React, { Fragment, useContext, useMemo } from 'react';

import { ModernHotelContext } from 'app/context/HotelContext/ModernHotelContext';
import { useRoomCategories } from 'app/hooks/data/rooms/useRoomCategories';

import { InventoryTabItemName } from '../../../../../InventoryStore';
import { useRowState } from '../../../../../hooks/useRowState';
import { CollapsibleTableRow } from '../../../../Table/CollapsibleTableRow/CollapsibleTableRow';
import { SectionRow } from '../../../../Table/SectionRow/SectionRow';

import { PackRoomCatContentRow } from './PackRoomCatContent';
import { RoomPackagePriceDiscountRow } from './RoomPackagePriceDiscountRow';

const PackRoomCatRowComponent: React.FC<{
  packId: number;
  packName: string;
}> = ({ packId, packName }) => {
  const { publishedRooms, getPack } = useContext(ModernHotelContext);
  const { inventoryPacks } = useRowState(InventoryTabItemName.Packs);
  const { data: roomsCategories } = useRoomCategories();

  const pack = getPack(packId);

  const categoriesMap = useMemo(
    () => new Map(roomsCategories?.map((cat) => [cat.id, cat.name]) ?? []),
    [roomsCategories]
  );

  const sortedRooms = useMemo(
    () =>
      [...publishedRooms]
        .filter((room) => pack?.packRooms.some((pr) => pr.roomId === room.id))
        .sort((a, b) => {
          const priorityA =
            pack?.packRooms.find((pr) => pr.roomId === a.id)?.priority ??
            Infinity;
          const priorityB =
            pack?.packRooms.find((pr) => pr.roomId === b.id)?.priority ??
            Infinity;

          return priorityA - priorityB;
        }),
    [publishedRooms, pack]
  );

  if (!inventoryPacks) {
    return null;
  }

  return (
    <CollapsibleTableRow
      panelKey={`room-cat-${packId}`}
      label="Room cat. on sale"
      headerContent={<PackRoomCatContentRow packId={packId} />}
    >
      {sortedRooms.map((room) => {
        const roomName =
          room.name || categoriesMap.get(room.categoryId) || room.categoryName;

        return (
          <Fragment key={`section-${packId}-${room.id}`}>
            <SectionRow labels={[packName, roomName]} />
            <RoomPackagePriceDiscountRow packId={packId} roomId={room.id} />
          </Fragment>
        );
      })}
    </CollapsibleTableRow>
  );
};

export const PackRoomCatRow = React.memo(PackRoomCatRowComponent);
